import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CannedResponseAPI from '../../api/cannedResponse';

let latestSearchRequest = 0;
let latestComposerRequest = 0;

const state = {
  records: [],
  composerRecords: [],
  recordsAccountId: null,
  composerAccountId: null,
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    deletingItem: false,
  },
};

const getters = {
  getComposerCannedResponses(_state) {
    if (
      _state.composerAccountId &&
      _state.composerAccountId !== CannedResponseAPI.accountIdFromRoute
    )
      return [];
    return _state.composerRecords;
  },
  getCannedResponses(_state) {
    if (
      _state.recordsAccountId &&
      _state.recordsAccountId !== CannedResponseAPI.accountIdFromRoute
    )
      return [];
    return _state.records;
  },
  getSortedCannedResponses(_state) {
    return sortOrder =>
      [...getters.getCannedResponses(_state)].sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.short_code.localeCompare(b.short_code);
        }
        return b.short_code.localeCompare(a.short_code);
      });
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

const actions = {
  async getComposerCannedResponses({ commit }, { searchKey } = {}) {
    latestComposerRequest += 1;
    const requestId = latestComposerRequest;
    const accountId = CannedResponseAPI.accountIdFromRoute;
    commit('SET_COMPOSER_CANNED', []);
    try {
      const { data } = await CannedResponseAPI.get({
        searchKey,
        context: 'composer',
      });
      if (
        requestId === latestComposerRequest &&
        accountId === CannedResponseAPI.accountIdFromRoute
      ) {
        commit('SET_COMPOSER_ACCOUNT', accountId);
        commit('SET_COMPOSER_CANNED', data);
      }
    } catch {
      // A failed request must not restore old suggestions.
    }
  },
  clearComposerCannedResponses({ commit }) {
    latestComposerRequest += 1;
    commit('SET_COMPOSER_CANNED', []);
  },
  clearCannedResponses({ commit }) {
    latestSearchRequest += 1;
    commit(types.default.SET_CANNED, []);
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
  },

  getCannedResponse: async function getCannedResponse(
    { commit },
    { searchKey } = {}
  ) {
    latestSearchRequest += 1;
    const requestId = latestSearchRequest;
    const accountId = CannedResponseAPI.accountIdFromRoute;
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });
    try {
      const response = await CannedResponseAPI.get({ searchKey });
      if (
        requestId !== latestSearchRequest ||
        accountId !== CannedResponseAPI.accountIdFromRoute
      )
        return;
      commit('SET_CANNED_ACCOUNT', accountId);
      commit(types.default.SET_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    } catch (error) {
      if (
        requestId !== latestSearchRequest ||
        accountId !== CannedResponseAPI.accountIdFromRoute
      )
        return;
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    }
  },

  createCannedResponse: async function createCannedResponse(
    { commit },
    cannedObj
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.create(cannedObj);
      commit(types.default.ADD_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  updateCannedResponse: async function updateCannedResponse(
    { commit },
    { id, ...updateObj }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const response = await CannedResponseAPI.update(id, updateObj);
      commit(types.default.EDIT_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  deleteCannedResponse: async function deleteCannedResponse({ commit }, id) {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.delete(id);
      commit(types.default.DELETE_CANNED, id);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return id;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return throwErrorMessage(error);
    }
  },
};

const mutations = {
  SET_CANNED_ACCOUNT(_state, accountId) {
    _state.recordsAccountId = accountId;
  },
  SET_COMPOSER_ACCOUNT(_state, accountId) {
    _state.composerAccountId = accountId;
  },
  SET_COMPOSER_CANNED(_state, records) {
    _state.composerRecords = records;
  },
  [types.default.SET_CANNED_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_CANNED]: MutationHelpers.set,
  [types.default.ADD_CANNED]: MutationHelpers.create,
  [types.default.EDIT_CANNED]: MutationHelpers.update,
  [types.default.DELETE_CANNED]: MutationHelpers.destroy,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
